<script setup>
import { ref, watch, computed } from 'vue'
import { onMounted } from 'vue'

const props = defineProps({
  listadosEntidadesStore: Object,
  carteleraStore: Object,
});

const filtros = ref({
  industrias: [],
  ubicaciones: []
});

const subastas = computed(() => props.carteleraStore?.subastas?.data ?? []);
const mostrarFiltros = ref(true);
const estado = ref('todas');
const searchId = ref('');

const industriasOpts = ref([]);
const ubicacionesOpts = ref([]);

watch(subastas, (arr) => {
  if (arr.length && industriasOpts.value.length === 0 && ubicacionesOpts.value.length === 0) {
    const mapInd = new Map();
    const mapUbi = new Map();

    for (const s of arr) {
      for (const it of (s.industrias || [])) {
        const id = it.industria?.id ?? it.industria_id ?? it.id;
        const nombre = it.industria?.nombre ?? it.nombre;
        if (id && nombre && !mapInd.has(id)) mapInd.set(id, { id, nombre });
      }
      for (const u of (s.ubicaciones || [])) {
        const id = u.id;
        const nombre = u.direccion
          ? `${u.direccion} ${u.ciudad}`
          : `${u.descripcion ?? ''}${u.descripcion ? ' - ' : ''}${u.ciudad ?? ''}`.trim();
        if (id && nombre && !mapUbi.has(id)) mapUbi.set(id, { id, nombre });
      }
    }

    industriasOpts.value = Array.from(mapInd.values());
    ubicacionesOpts.value = Array.from(mapUbi.values());
  }
}, { immediate: true });

function armarPayload() {
  const payload = {
    'industrias:industria_id': filtros.value.industrias.map(t => t.id),
    'ubicaciones:id': filtros.value.ubicaciones.map(u => u.id),
  };

  if (estado.value === 'vigentes') {
    payload['estado_subasta:slug'] = ['en-curso', 'confirmada'];
  } else if (estado.value === 'finalizadas') {
    payload['estado_subasta:slug'] = ['finalizada'];
  }

  if (searchId.value.trim()) {
    payload['external_id'] = searchId.value.trim();
  }

  return payload;
}

function enviarPayload() {
  console.log('Enviando filtros:', armarPayload());
  props.carteleraStore?.getSubastas?.(armarPayload());
}

function toggleFiltro(tipo, valorObj) {
  const i = filtros.value[tipo].findIndex(f => f.id === valorObj.id);
  if (i === -1) filtros.value[tipo].push(valorObj);
  else filtros.value[tipo].splice(i, 1);

  enviarPayload(); 
}

watch(estado, () => {
  enviarPayload();
});

watch(searchId, () => {
  enviarPayload();
});

onMounted(() => {
  if (window.innerWidth < 768) {
    mostrarFiltros.value = false;
  }

  if (
    filtros.value.industrias.length ||
    filtros.value.ubicaciones.length ||
    estado.value !== 'todas' ||
    searchId.value.trim()
  ) {
    enviarPayload();
  }
});
</script>

<template>
  <div class="mb-3 rounded-3">
    <div
      class="btn btn-white text-soft-green flex-fill shadow-none w-100 text-start d-flex align-items-center justify-content-between collapsed"
      data-bs-toggle="collapse" data-bs-target="#subastasVigentes" aria-expanded="false"
      aria-controls="subastasVigentes" role="button" tabindex="0">
      <span>Subastas
        {{
          estado === 'todas'
            ? 'todas'
            : estado === 'vigentes'
              ? 'vigentes'
              : 'finalizadas'
        }}
      </span>
      <i class="fa-solid fa-chevron-down chev fs-6"></i>
    </div>

    <div id="subastasVigentes" class="collapse">
      <div class="card card-body border-0 shadow-none rounded-0 text-soft-green fw-bold fs-14">
        <div class="d-flex align-items-center py-2" role="button" @click="estado = 'todas'">
          <span :class="{ '': estado === 'todas' }">Todas</span>
          <input class="form-check-input ms-auto" type="radio" name="estadoSubastas" value="todas" v-model="estado">
        </div>
        <div class="d-flex align-items-center py-2" role="button" @click="estado = 'vigentes'">
          <span :class="{ '': estado === 'vigentes' }">Vigentes</span>
          <input class="form-check-input ms-auto" type="radio" name="estadoSubastas" value="vigentes" v-model="estado">
        </div>

        <div class="d-flex align-items-center py-2" role="button" @click="estado = 'finalizadas'">
          <span :class="{ '': estado === 'finalizadas' }">Finalizadas</span>
          <input class="form-check-input ms-auto" type="radio" name="estadoSubastas" value="finalizadas"
            v-model="estado">
        </div>
      </div>
    </div>
  </div>

  <div class="card card-body border-0 shadow-none rounded-0 p-0 mt-3 rounded-2">
    <div class="input-group padding">
      <input type="text" class="form-control border-0 shadow-none rounded-2 fs-14" placeholder="Buscar por ID"
        v-model="searchId" @keyup.enter="searchId()">

      <span class="input-group-text bg-white border-0">
        <i class="fa-solid fa-magnifying-glass text-soft-green"></i>
      </span>
    </div>
  </div>
  <div class="shadow-none">


    <div class="card-body bg-white mt-3 rounded-2 pb-1 pt-0 pb-lg-3 pt-lg-3">
      <div class="card-header d-flex flex-wrap align-items-center gap-2 pb-0 pb-lg-3 border-0 px-0">
        <div class="d-flex justify-content-between align-items-center w-100">
          <p class="mb-0 fs-5 d-flex align-items-center fw-semiblod gap-2">
            <img src="@/assets/img/icons/layer.svg" alt="">Filtros
          </p>
          <div class="d-flex align-items-center gap-3">
            <i class="cursor-pointer d-md-none" :class="mostrarFiltros ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
              @click="mostrarFiltros = !mostrarFiltros"></i>
          </div>
        </div>

        <div class="w-100 mt-2">
          <div class="d-flex flex-wrap gap-2">
            <template v-for="(valores, tipo) in filtros" :key="tipo">
              <template v-for="valor in valores" :key="valor.id">
                <span
                  class="badge bg-badge-secondary text-white d-flex align-items-center gap-2 px-2 py-2 cursor-pointer fs-7"
                  style="white-space: normal; word-break: break-word;" @click="toggleFiltro(tipo, valor)">
                  <span>{{ valor.nombre }}</span>
                  <i class="fa fa-times"></i>
                </span>
              </template>
            </template>
          </div>
        </div>
      </div>

      <div class="pt-0" v-if="mostrarFiltros">
        <p class="mb-2 fs-5">Industrias</p>
        <ul class="list-unstyled">
          <li v-for="item in industriasOpts" :key="'ind-' + item.id" class="mb-2">
            <a href="javascript:;" class="text-subtitulos-secondary text-decoration-none"
              @click="toggleFiltro('industrias', item)"
              :class="{ 'text-soft-primary fw-semibold': filtros.industrias.some(f => f.id === item.id) }">
              {{ item.nombre }}
            </a>
          </li>
        </ul>

        <p class="mt-3 mb-2 fs-5">Ubicación</p>
        <ul class="list-unstyled fw-bold">
          <li v-for="item in ubicacionesOpts" :key="'ubi-' + item.id" class="mb-2">
            <a href="javascript:;" class="text-subtitulos-secondary text-decoration-none"
              @click="toggleFiltro('ubicaciones', item)"
              :class="{ 'text-soft-primary fw-semibold': filtros.ubicaciones.some(f => f.id === item.id) }">
              {{ item.nombre }}
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>


<style scoped>
a:hover {
  color: #006C4C !important;
}

.btn:hover {
  transform: none !important;
  box-shadow: none !important;
}

.card-body {
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

.padding {
  padding-left: 0.50rem;
  padding-right: 0.50rem;
}


.input-group .form-control {
  border-left: none !important;
  border-right: none !important;
}

.input-group .form-control:focus {
  box-shadow: none !important;
  outline: none !important;
  border-color: transparent !important;
  border-left: none !important;
  border-right: none !important;
}
</style>